import { ApiError, MAX_SOURCE_GRACE_MS, type User } from '../api'
import { refusalText } from '../billing'

/*
 * Source grace (D21; docs/architecture/routing-network/backend/04-admin-api.md §6):
 * how long the platforms stay connected after OBS drops. A stream key and each
 * destination may have their own (0–600 s); null = inherit: a destination takes
 * its key's, a key the center's default (GET /api/ingest default_source_grace_ms).
 * The form edits whole seconds; the API speaks ms.
 */

export const GRACE_MAX_S = MAX_SOURCE_GRACE_MS / 1000

/** "0 s (off)", "10 s", "1 min 30 s", "10 min". */
export function graceText(ms: number | null | undefined): string {
  if (ms == null) return '—'
  const s = Math.round(ms / 1000)
  if (s === 0) return '0 s (off)'
  if (s < 60) return `${s} s`
  const m = Math.floor(s / 60)
  return s % 60 ? `${m} min ${s % 60} s` : `${m} min`
}

/** The seconds typed into the field → ms, or why not (checked here before the server sees it). */
export function parseGrace(text: string): { ms?: number; error?: string } {
  const t = text.trim()
  if (!t) return { error: `Enter seconds, 0 to ${GRACE_MAX_S}` }
  if (!/^\d+$/.test(t)) return { error: `Whole seconds only, 0 to ${GRACE_MAX_S}` }
  const s = Number(t)
  if (s > GRACE_MAX_S) return { error: `At most ${GRACE_MAX_S} s (10 min)` }
  return { ms: s * 1000 }
}

/** A failed save of a grace in words: 400 (the center refused the value), 409 (revoked key), the rest. */
export function graceSaveError(e: unknown, user?: User | null): string {
  if (e instanceof ApiError) {
    // above the plan's ceiling (422 plan_limit_source_grace, BILL-1 B2 in enforce)
    const plan = e.code === 'plan_limit_source_grace' ? refusalText(e.code, user, e.data) : undefined
    if (plan) return plan
    if (e.status === 400) return `Not saved: the server refused this value (${e.message}). Your previous setting stays.`
    if (e.status === 409) return 'Not saved: this stream key is revoked. Issue a new key to change its settings.'
    if (e.status === 404) return 'Not saved: this key no longer exists. Reload the page.'
    if (e.status >= 500) return `Not saved: the server did not answer (${e.message}). Try again.`
  }
  return `Not saved: ${(e as Error).message}. Try again.`
}
